<template>
  <div class="w-100">
    <!-- Terminal posted state -->
    <div v-if="isTerminalPosted" class="text-center">
      <b-alert variant="success" show class="mb-0">
        <div class="text-dark font-weight-bolder font-size-h5">
          <i class="fa fa-check-circle text-success"></i>
          POSTED
        </div>
        <div class="text-dark">
          This transaction has been posted.
          <span v-if="displayInvoice"> Invoice {{ displayInvoice }}.</span>
          It cannot be posted again.
        </div>
      </b-alert>
    </div>

    <!-- Unknown outcome: no retry -->
    <div v-else-if="outcomeUnknown" class="text-center">
      <b-alert variant="danger" show class="mb-0">
        <div class="text-dark font-weight-bolder font-size-h5">
          <i class="fa fa-exclamation-triangle text-danger"></i>
          Outcome unclear — do not post again
        </div>
        <div class="text-dark mb-4">{{ copy.OUTCOME_UNKNOWN }}</div>
        <button
          type="button"
          class="btn btn-warning font-weight-bold"
          :disabled="checkingStatus"
          @click.prevent="checkRecorded"
        >
          <i class="fa fa-search" :class="{ 'fa-spin': checkingStatus }"></i>
          Check existing attempt (read-only)
        </button>
        <div v-if="statusCheckDetail" class="text-dark font-size-sm mt-3">
          {{ statusCheckDetail }}
        </div>
      </b-alert>
    </div>

    <!-- Posted reconciliation hold: billing/CRM retry only (never re-post) -->
    <div v-else-if="isReconciliationHold" class="text-center">
      <b-alert variant="warning" show class="mb-0">
        <div class="text-dark font-weight-bolder font-size-h5">
          Posted — finishing records
        </div>
        <div class="text-dark mb-3">
          The invoice is already in INSIGHT. Do not post again.
          Retry saving the billing record and updating the Zoho transaction if either is still waiting.
        </div>
        <button
          type="button"
          class="btn btn-warning font-weight-bold"
          :disabled="continuingReconcile"
          @click.prevent="retryReconcile"
        >
          <i class="fa fa-sync" :class="{ 'fa-spin': continuingReconcile }"></i>
          Retry record updates (does not post again)
        </button>
        <div v-if="reconcileRetryDetail" class="text-dark font-size-sm mt-3">
          {{ reconcileRetryDetail }}
        </div>
        <div class="text-muted font-size-sm mt-2">
          Money has already moved. This retry never posts a second invoice.
        </div>
      </b-alert>
    </div>

    <!-- Unposted: gated post action -->
    <div v-else class="d-flex flex-column align-items-center">
      <button
        type="button"
        class="btn btn-success btn-lg font-weight-bold px-9 py-4"
        :disabled="!armed || posting || !canOpenConfirm || isWaitingToArm"
        @click.prevent="openConfirm"
      >
        <i
          class="fa"
          :class="isWaitingToArm ? 'fa-spinner fa-spin' : 'fa-file-invoice-dollar'"
        ></i>
        {{ openPostButtonLabel }}
      </button>
      <div v-if="isWaitingToArm" class="text-info font-size-sm mt-3 text-center">
        Checking post readiness… {{ armingElapsedSec }}s
        <div class="text-muted mt-1">
          The post button unlocks when the draft, schedule, and invoice checks finish.
        </div>
      </div>
      <div v-else-if="!armed" class="text-muted font-size-sm mt-3 text-center">
        {{ copy.DISARMED_HELPER }}
      </div>
      <div v-else-if="!canOpenConfirm" class="text-muted font-size-sm mt-3 text-center">
        Waiting for who we're invoicing and the payable amount before confirmation can open.
      </div>
      <div v-else class="text-dark font-size-sm mt-3 text-center">
        Ready to confirm post. This button opens review only — money moves after you confirm.
      </div>
      <div v-if="refusalText" class="text-danger font-size-sm mt-3 text-center" style="max-width: 36rem">
        {{ refusalText }}
      </div>
    </div>

    <!--begin: Post-dispatch forensic — what INSIGHT holds for the commission split now.
        The pre-post read-back proves the AR lines were present immediately before dispatch, so an
        allocation missing here was lost at posting. Read-only: one GetEx, nothing is written. -->
    <div v-if="showAllocationPanel" class="mt-5">
      <div class="d-flex flex-wrap justify-content-between align-items-center mb-2">
        <div class="font-weight-bold text-dark">
          Authorised rep allocation on the posted transaction
        </div>
        <button
          type="button"
          class="btn btn-sm btn-light-primary font-weight-bold"
          :disabled="allocationLoading"
          @click.prevent="loadPostedAllocation"
        >
          <i class="fa fa-not-equal" :class="{ 'fa-spin': allocationLoading }"></i>
          {{ allocationReport ? "Re-check split" : "Check the commission split" }}
        </button>
      </div>

      <div v-if="allocationSummary" class="font-size-sm mb-2" :class="allocationSummaryClass">
        {{ allocationSummary }}
      </div>
      <div v-if="allocationError" class="text-danger font-size-sm mb-2">
        {{ allocationError }}
      </div>

      <template v-if="allocationReport">
        <div class="text-dark font-size-sm mb-2">{{ allocationReport.verdict }}</div>
        <table v-if="allocationRows.length" class="table table-sm table-borderless mb-2 font-size-sm">
          <thead>
            <tr class="text-muted">
              <th>Position</th>
              <th>Reviewed draft</th>
              <th>INSIGHT now</th>
              <th>Commission</th>
              <th>Fee</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in allocationRows" :key="'posted-ar' + row.label">
              <td class="font-weight-bold text-dark">{{ row.label }}</td>
              <td>{{ displayValue(row.expectedAuthorisedRepId) }}</td>
              <td :class="row.authorisedRepMatches ? '' : 'text-danger'">
                {{ displayValue(row.actualAuthorisedRepId) }}
              </td>
              <td :class="moneyClass(row, 'Commission')">
                {{ displayValue(moneyOf(row, "Commission").expected) }}
                <span v-if="!moneyOf(row, 'Commission').match">
                  → {{ displayValue(moneyOf(row, "Commission").actual) }}
                </span>
              </td>
              <td :class="moneyClass(row, 'Fee')">
                {{ displayValue(moneyOf(row, "Fee").expected) }}
                <span v-if="!moneyOf(row, 'Fee').match">
                  → {{ displayValue(moneyOf(row, "Fee").actual) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="text-muted font-size-sm">
          Invoice debtor INSIGHT derived:
          {{ displayValue(allocationReport.derivedDebtorId) }}
          <span v-if="allocationReport.derivedDebtorKey">
            (read from {{ allocationReport.derivedDebtorKey }})
          </span>
          — the producing broker this transaction bills is
          {{ displayValue(allocationReport.expectedProducingBrokerId) }}.
        </div>
      </template>
    </div>
    <!--end: Post-dispatch forensic -->

    <b-modal
      ref="confirmModal"
      title="Confirm post once — money moves on OK"
      size="lg"
      no-close-on-backdrop
      hide-header-close
      :ok-title="postOkTitle"
      :ok-disabled="!confirmReady || posting"
      :cancel-disabled="posting"
      cancel-title="Cancel"
      @show="onModalShow"
      @ok="onConfirmOk"
      @hidden="onModalHidden"
    >
      <div class="text-dark">
        <b-alert variant="warning" show class="mb-3">
          This posts to INSIGHT exactly once. Outcome-unknown and reconciliation holds never re-arm posting.
        </b-alert>
        <b-alert v-if="posting" variant="info" show class="mb-3">
          <div class="font-weight-bold text-dark">
            <i class="fa fa-spinner fa-spin mr-1"></i>
            Sending to INSIGHT… {{ postingElapsedSec }}s
          </div>
          <div class="text-dark font-size-sm mt-1">
            Checking the draft, then posting. Do not close this window or click again.
          </div>
        </b-alert>
        <div class="mb-3">
          <div class="font-size-sm">
            <strong>Policy:</strong> {{ policyNumber || "—" }}
          </div>
          <div class="font-size-sm">
            <strong>Payable amount (confirm this):</strong> {{ formattedMoney }}
          </div>
          <div v-if="underwritingBase != null" class="font-size-sm text-muted">
            Underwriting base (not the post amount): {{ formatMoney(underwritingBase) }}
          </div>
          <div v-if="annualPayable != null" class="font-size-sm text-muted">
            Annual payable (K_StateCharges): {{ formatMoney(annualPayable) }}
          </div>
          <div class="font-size-sm">
            <strong>{{ copy.MODAL_WHO_WE_ARE_INVOICING }}:</strong>
            {{ debtorLabel || "—" }}
          </div>
          <div class="font-size-sm">
            <strong>Debtor INSIGHT AR id:</strong> {{ expectedDebtorId != null ? expectedDebtorId : "—" }}
          </div>
          <div v-if="attemptId" class="font-size-sm">
            <strong>Attempt id:</strong> {{ attemptId }}
          </div>
          <div class="font-size-sm mt-2">
            <strong>Commission proof</strong>
            <ul class="mb-0 pl-4">
              <li v-for="(line, idx) in commissionProofLines" :key="idx">{{ line }}</li>
            </ul>
          </div>
          <div class="font-size-sm text-muted mt-2">
            Duplicate prevention: one idempotency key per confirmation; never post again for this attempt.
          </div>
        </div>

        <div class="form-group">
          <label class="font-weight-bold">{{ copy.MODAL_ACCOUNTING_MONTH }}</label>
          <input
            v-model="accountingMonthInput"
            type="text"
            class="form-control"
            placeholder="YYYYMM"
            autocomplete="off"
          />
          <small
            v-if="accountingMonthInput && !accountingMonthValid"
            class="text-danger"
          >
            INSIGHT accepts 0 (current month) or six digits YYYYMM only.
          </small>
          <small v-else class="text-muted">
            0 posts into the current accounting month; otherwise six digits, e.g. 202607.
          </small>
        </div>

        <div class="form-group">
          <label class="font-weight-bold">Type the policy number to confirm</label>
          <input
            v-model="policyNumberTyped"
            type="text"
            class="form-control"
            autocomplete="off"
          />
        </div>

        <div class="form-group mb-0">
          <b-form-checkbox v-model="humanConfirmation" :value="true">
            {{ copy.MODAL_YOUR_CONFIRMATION }} — I have checked the amount and who we're invoicing.
          </b-form-checkbox>
        </div>

        <!--
          A refusal MUST be readable where the operator is looking. The modal stays
          open on refusal (onConfirmOk preventDefaults), so the copy of this message
          rendered behind the modal is invisible and the click looks like a no-op.
        -->
        <b-alert v-if="refusalText" variant="warning" show class="mt-4 mb-0">
          <div class="font-weight-bold text-dark">This post was refused — nothing was sent to INSIGHT</div>
          <div class="text-dark">{{ refusalText }}</div>
          <div v-if="refusalCode" class="text-muted font-size-sm mt-1">
            Reason code: <code>{{ refusalCode }}</code>
          </div>
        </b-alert>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  post,
  postingStatus,
  postedAllocation,
  continueReconcile
} from "@/services/insight-post-service";
import {
  BUTTON_POST_TRANSACTION,
  DISARMED_HELPER,
  OUTCOME_UNKNOWN,
  MODAL_YOUR_CONFIRMATION,
  MODAL_WHO_WE_ARE_INVOICING,
  MODAL_ACCOUNTING_MONTH,
  CHECK_WHAT_INSIGHT_RECORDED,
  refusalMessage
} from "@/copy/posting-copy";

export default {
  name: "PostGate",

  props: {
    armed: {
      type: Boolean,
      default: false
    },
    // 'unposted' | 'posted' | 'confirmed' | 'outcome_unknown'
    status: {
      type: String,
      default: "unposted"
    },
    policyNumber: {
      type: String,
      default: ""
    },
    expectedMoney: {
      type: [Number, String],
      default: null
    },
    expectedDebtorId: {
      type: [Number, String],
      default: null
    },
    debtorLabel: {
      type: String,
      default: ""
    },
    accountingMonth: {
      type: [String, Number],
      default: ""
    },
    // Billing-line id (K_IbaisTransactions record id)
    billingLineId: {
      type: String,
      default: null
    },
    // Backward-compat alias used by older parents
    ledgerRowId: {
      type: String,
      default: null
    },
    invoiceNumber: {
      type: [String, Number],
      default: null
    },
    commissionConfig: {
      type: Object,
      default: null
    },
    arSplit: {
      type: Array,
      default: function () {
        return [];
      }
    },
    annualPayable: {
      type: [Number, String],
      default: null
    },
    underwritingBase: {
      type: [Number, String],
      default: null
    },
    attemptId: {
      type: [String, Number],
      default: null
    },
    // Sole post-open signal from policy-processing-readiness.v1 (parent must load it).
    processingReadiness: {
      type: Object,
      default: null
    },
    readinessLoading: {
      type: Boolean,
      default: false
    },
    scheduleAttached: {
      type: Boolean,
      default: false
    },
    hasDraft: {
      type: Boolean,
      default: false
    }
  },

  data: function() {
    return {
      copy: {
        BUTTON_POST_TRANSACTION: BUTTON_POST_TRANSACTION,
        DISARMED_HELPER: DISARMED_HELPER,
        OUTCOME_UNKNOWN: OUTCOME_UNKNOWN,
        MODAL_YOUR_CONFIRMATION: MODAL_YOUR_CONFIRMATION,
        MODAL_WHO_WE_ARE_INVOICING: MODAL_WHO_WE_ARE_INVOICING,
        MODAL_ACCOUNTING_MONTH: MODAL_ACCOUNTING_MONTH,
        CHECK_WHAT_INSIGHT_RECORDED: CHECK_WHAT_INSIGHT_RECORDED
      },
      posting: false,
      postingLocked: false,
      postingElapsedSec: 0,
      postingTickHandle: null,
      armingElapsedSec: 0,
      armingTickHandle: null,
      outcomeUnknown: false,
      confirmedPosted: false,
      confirmedInvoice: null,
      checkingStatus: false,
      statusCheckDetail: null,
      continuingReconcile: false,
      reconcileRetryDetail: null,
      allocationLoading: false,
      allocationReport: null,
      allocationError: null,
      // The server's own post-post allocation verdict, carried on posting status so it survives
      // the posting response being gone.
      allocationCheck: null,
      refusalText: null,
      refusalCode: null,
      // Generated once per modal open; never shown
      postingReference: null,
      accountingMonthInput: "",
      policyNumberTyped: "",
      humanConfirmation: false
    };
  },

  beforeDestroy: function() {
    this.stopPostingTick();
    this.stopArmingTick();
  },

  watch: {
    isWaitingToArm: {
      immediate: true,
      handler: function(active) {
        if (active) {
          this.startArmingTick();
        } else {
          this.stopArmingTick();
        }
      }
    }
  },

  computed: {
    rowId: function() {
      return this.billingLineId || this.ledgerRowId || null;
    },

    /**
     * True while the post button is still locked after draft/schedule work, or while
     * readiness/debtor checks are in flight. Avoids a dead-looking disabled button.
     */
    isWaitingToArm: function() {
      if (
        this.isTerminalPosted
        || this.outcomeUnknown
        || this.isReconciliationHold
        || this.posting
        || this.postingLocked
      ) {
        return false;
      }
      if (this.readinessLoading) {
        return true;
      }
      // Schedule just attached; readiness response not in yet.
      if (
        this.hasDraft
        && this.scheduleAttached
        && !this.armed
        && this.processingReadiness == null
      ) {
        return true;
      }
      // Readiness armed the gate, but debtor/money still catching up.
      if (this.armed && !this.canOpenConfirm) {
        return true;
      }
      return false;
    },

    openPostButtonLabel: function() {
      if (this.isWaitingToArm) {
        return "Preparing post… " + this.armingElapsedSec + "s";
      }
      return "Open post confirmation";
    },

    postOkTitle: function() {
      if (this.posting) {
        return "Posting… " + this.postingElapsedSec + "s";
      }
      return "Post once";
    },

    isTerminalPosted: function() {
      const s = String(this.status || "").toLowerCase();
      if (
        s.indexOf("reconciliation_hold") >= 0
        || s === "post_confirmed"
        || s === "neon_recorded"
        || s === "zoho_recorded"
      ) {
        return false;
      }
      if (this.confirmedPosted) {
        return true;
      }
      return s === "posted" || s === "confirmed" || s === "reconciled";
    },

    isReconciliationHold: function() {
      const s = String(this.status || "").toLowerCase();
      return s.indexOf("reconciliation_hold") >= 0
        || s === "reconcile_readback_started"
        || s === "post_confirmed"
        || s === "neon_recorded"
        || s === "zoho_recorded";
    },

    commissionProofLines: function() {
      const lines = [];
      const cfg = this.commissionConfig || {};
      if (Array.isArray(this.arSplit) && this.arSplit.length) {
        this.arSplit.forEach((ar) => {
          const name = ar.roleLabel || ar.role || "Commission share";
          const id = ar.authorisedRepId != null ? ` (INSIGHT id ${ar.authorisedRepId})` : "";
          lines.push(`${name}${id}: ${ar.pct}%`);
        });
      } else {
        lines.push(`Producing broker: ${cfg.producingBrokerPercent != null ? cfg.producingBrokerPercent + "%" : "missing"}`);
        lines.push(`Fuse income: ${cfg.fusePercent != null ? cfg.fusePercent + "%" : "missing"}`);
        lines.push(`Guy Carpenter: ${cfg.guyCarpenterPercent != null ? cfg.guyCarpenterPercent + "%" : "missing"}`);
      }
      if (cfg.totalBrokeragePercent != null) {
        lines.push(`Total brokerage ${cfg.totalBrokeragePercent}%`);
      }
      return lines;
    },

    displayInvoice: function() {
      return this.confirmedInvoice || this.invoiceNumber || null;
    },

    canOpenConfirm: function() {
      const readiness = this.processingReadiness || {};
      if (
        readiness.contractVersion !== "policy-processing-readiness.v1"
        || readiness.nextCommand !== "review-post"
        || readiness.error
        || readiness.holdClass === "outcome_unknown"
        || readiness.holdClass === "reconciliation_hold"
      ) {
        return false;
      }
      return !!this.rowId && this.expectedMoney != null && this.expectedDebtorId != null;
    },

    /** Only meaningful once something has actually been dispatched to INSIGHT. */
    showAllocationPanel: function() {
      return this.isTerminalPosted || this.outcomeUnknown;
    },

    allocationRows: function() {
      const allocation = this.allocationReport && this.allocationReport.arAllocation;
      return allocation && Array.isArray(allocation.rows) ? allocation.rows : [];
    },

    /** Prefer the live re-read; fall back to the verdict the post rail recorded. */
    allocationSummary: function() {
      if (this.allocationReport) {
        return null;
      }
      return this.allocationCheck ? this.allocationCheck.detail : null;
    },

    allocationSummaryClass: function() {
      if (!this.allocationCheck) {
        return "text-muted";
      }
      if (this.allocationCheck.matches === false) {
        return "text-danger";
      }
      return this.allocationCheck.matches === true ? "text-success" : "text-muted";
    },

    // INSIGHT accepts 0 (current accounting month) or six-digit YYYYMM and nothing else.
    accountingMonthValid: function() {
      const value = normaliseAccountingMonth(this.accountingMonthInput);
      if (value === "0") {
        return true;
      }
      if (!/^\d{6}$/.test(value)) {
        return false;
      }
      const month = Number(value.slice(4));
      return month >= 1 && month <= 12;
    },

    confirmReady: function() {
      if (!this.humanConfirmation) {
        return false;
      }
      if (!this.accountingMonthValid) {
        return false;
      }
      const typed = normalisePolicyIdentity(this.policyNumberTyped);
      const expected = normalisePolicyIdentity(this.policyNumber);
      return !!typed && !!expected && typed === expected;
    },

    /** Names the specific confirm field that is still outstanding. */
    confirmBlockedReason: function() {
      if (!this.humanConfirmation) {
        return "Tick " + this.copy.MODAL_YOUR_CONFIRMATION + " before posting.";
      }
      if (!this.accountingMonthValid) {
        return (
          "The " +
          this.copy.MODAL_ACCOUNTING_MONTH.toLowerCase() +
          " must be 0 (current month) or six digits, e.g. 202607."
        );
      }
      const typed = String(this.policyNumberTyped || "").trim();
      const expected = String(this.policyNumber || "").trim();
      if (!expected) {
        return "This screen does not have a policy number to check against. Reload the page.";
      }
      if (!typed) {
        return "Type the policy number " + expected + " to confirm.";
      }
      if (normalisePolicyIdentity(typed) !== normalisePolicyIdentity(expected)) {
        return (
          'The policy number you typed ("' +
          typed +
          '") does not match ' +
          expected +
          ". Type it exactly."
        );
      }
      return "Complete the confirmation fields before posting.";
    },

    formattedMoney: function() {
      return this.formatMoney(this.expectedMoney);
    }
  },

  watch: {
    status: function(next) {
      const s = String(next || "").toLowerCase();
      if (s === "outcome_unknown" || s === "post_outcome_unknown") {
        this.outcomeUnknown = true;
      }
      if (s === "posted" || s === "confirmed" || s === "reconciled") {
        this.confirmedPosted = true;
      }
    }
  },

  mounted: function() {
    const s = String(this.status || "").toLowerCase();
    if (s === "outcome_unknown") {
      this.outcomeUnknown = true;
    }
    if (s === "posted" || s === "confirmed") {
      this.confirmedPosted = true;
    }
    this.syncPostingStatus();
  },

  methods: {
    formatMoney: function(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) {
        return "—";
      }
      try {
        return new Intl.NumberFormat("en-AU", {
          style: "currency",
          currency: "AUD",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(n);
      } catch (err) {
        return n.toFixed(2);
      }
    },

    syncPostingStatus: function() {
      const id = this.rowId;
      if (!id || typeof postingStatus !== "function") {
        return Promise.resolve(null);
      }
      return postingStatus(id)
        .then(result => {
          if (!result) return null;
          if (result.allocationCheck) {
            this.allocationCheck = result.allocationCheck;
          }
          if (result.writePlan || result.attemptId || result.postingAttemptId || result.status) {
            this.$emit("status-progress", {
              writePlan: result.writePlan || null,
              attemptId: result.attemptId || result.postingAttemptId || null,
              status: result.status || null
            });
          }
          const st = String(result.status || "").toLowerCase();
          if (st === "outcome_unknown" || st === "sending" || st === "post_outcome_unknown") {
            this.outcomeUnknown = true;
            this.postingLocked = true;
          }
          if (st === "confirmed" || st === "posted" || st === "reconciled") {
            this.confirmedPosted = true;
            this.postingLocked = true;
          }
          if (st === "none" || st === "authorised" || st === "failed") {
            // Draft may or may not exist; post path still refuses if schedule missing.
          }
          return result;
        })
        .catch(() => null);
    },

    openConfirm: function() {
      if (!this.armed || this.posting || this.postingLocked || !this.canOpenConfirm) {
        return;
      }
      this.syncPostingStatus().then(status => {
        if (this.outcomeUnknown || this.confirmedPosted || this.postingLocked) {
          return;
        }
        const st = status && String(status.status || "").toLowerCase();
        if (st === "none" || !status) {
          this.setRefusal("no_draft", refusalMessage("no_draft", {}));
          return;
        }
        if (st === "authorised" && status.scheduleAttached === false) {
          this.setRefusal(
            "schedule_not_attached",
            refusalMessage("schedule_not_attached", {})
          );
          return;
        }
        const modal = this.$refs.confirmModal;
        if (!modal || typeof modal.show !== "function") {
          this.setRefusal(
            "confirm_window_unavailable",
            "The confirmation window could not be opened. Reload the page and try again."
          );
          return;
        }
        modal.show();
      });
    },

    onModalShow: function() {
      this.postingReference = createPostingReference();
      this.policyNumberTyped = "";
      this.humanConfirmation = false;
      this.refusalText = null;
      this.refusalCode = null;
      const preset = this.accountingMonth;
      this.accountingMonthInput = preset
        ? normaliseAccountingMonth(preset)
        : defaultAccountingMonth();
    },

    onModalHidden: function() {
      // Keep postingReference until a successful dispatch; regenerate on next open.
      if (!this.postingLocked) {
        this.postingReference = null;
      }
    },

    onConfirmOk: function(bvModalEvt) {
      if (bvModalEvt && typeof bvModalEvt.preventDefault === "function") {
        bvModalEvt.preventDefault();
      }
      // Never return silently: an enabled button that does nothing is how an
      // operator ends up clicking repeatedly on a money flow.
      if (this.postingLocked) {
        this.setRefusal(
          "locked_for_this_attempt",
          "This attempt is already locked. Close this window and use " +
            this.copy.CHECK_WHAT_INSIGHT_RECORDED +
            " — do not post again."
        );
        return;
      }
      if (this.posting) {
        this.setRefusal("in_flight", "This post is already being sent. Wait for the result.");
        return;
      }
      if (!this.confirmReady) {
        this.setRefusal("confirmation_incomplete", this.confirmBlockedReason);
        return;
      }
      this.submitPost();
    },

    setRefusal: function(code, message) {
      this.refusalCode = code || null;
      this.refusalText = message;
    },

    startPostingTick: function() {
      this.stopPostingTick();
      this.postingElapsedSec = 0;
      const startedAt = Date.now();
      this.postingTickHandle = setInterval(() => {
        this.postingElapsedSec = Math.max(
          0,
          Math.floor((Date.now() - startedAt) / 1000)
        );
      }, 250);
    },

    stopPostingTick: function() {
      if (this.postingTickHandle) {
        clearInterval(this.postingTickHandle);
        this.postingTickHandle = null;
      }
    },

    startArmingTick: function() {
      this.stopArmingTick();
      this.armingElapsedSec = 0;
      const startedAt = Date.now();
      this.armingTickHandle = setInterval(() => {
        this.armingElapsedSec = Math.max(
          0,
          Math.floor((Date.now() - startedAt) / 1000)
        );
      }, 250);
    },

    stopArmingTick: function() {
      if (this.armingTickHandle) {
        clearInterval(this.armingTickHandle);
        this.armingTickHandle = null;
      }
      this.armingElapsedSec = 0;
    },

    submitPost: function() {
      const id = this.rowId;
      if (!id) {
        this.setRefusal(
          "billing_line_missing",
          "This screen has lost the billing line it was posting. Reload the page and try again."
        );
        return;
      }
      if (!this.postingReference) {
        this.setRefusal(
          "posting_reference_missing",
          "This post has no unique reference, which is what stops a double post. Close this window, open it again, and re-confirm."
        );
        return;
      }
      this.posting = true;
      this.startPostingTick();
      this.refusalText = null;
      this.refusalCode = null;

      const body = {
        accountingMonth: normaliseAccountingMonth(this.accountingMonthInput),
        humanConfirmation: true,
        policyNumberTyped: String(this.policyNumberTyped || "").trim(),
        expectedMoney: Number(this.expectedMoney),
        expectedDebtorId: this.expectedDebtorId
      };
      // Wire field name required by the server; never shown on screen.
      body[["idempot", "encyKey"].join("")] = this.postingReference;

      post(id, body)
        .then(result => {
          this.posting = false;
          this.stopPostingTick();
          if (!result) {
            this.setRefusal("unknown", refusalMessage("unknown", {}));
            return;
          }
          if (result.ok === false) {
            // Carry the engineer detail through when the code is one the copy map
            // does not name, so a new server refusal can never present as silence.
            const fields = result.fields || {};
            if (fields.message == null && (result.error || result.detail)) {
              fields.message = result.error || result.detail;
            }
            this.setRefusal(result.code || "unknown", refusalMessage(result.code, fields));
            return;
          }
          // Successful dispatch — never re-enable for this attempt.
          this.postingLocked = true;
          this.hideModal();
          const status = String(result.status || "").toLowerCase();
          if (
            status.indexOf("reconciliation_hold") >= 0
            || status === "post_confirmed"
            || status === "neon_recorded"
            || status === "zoho_recorded"
          ) {
            this.confirmedInvoice =
              result.invoiceNumber || result.invoice || this.invoiceNumber || null;
            this.$emit("status-progress", {
              writePlan: result.writePlan || null,
              attemptId: result.attemptId || result.postingAttemptId || null,
              status: result.status || "post_confirmed_reconciliation_hold"
            });
            this.$emit("posted", Object.assign({}, result, {
              status: result.status || "post_confirmed_reconciliation_hold"
            }));
            return;
          }
          if (
            status === "confirmed"
            || status === "posted"
            || status === "reconciled"
          ) {
            this.confirmedPosted = true;
            this.confirmedInvoice =
              result.invoiceNumber || result.invoice || this.invoiceNumber || null;
            this.$emit("posted", result);
            this.$emit("status-progress", {
              writePlan: result.writePlan || null,
              attemptId: result.attemptId || result.postingAttemptId || null,
              status: result.status || null
            });
            return;
          }
          // Default: treat as unclear — no retry.
          this.outcomeUnknown = true;
          this.$emit("outcome-unknown", result);
        })
        .catch(() => {
          this.posting = false;
          this.stopPostingTick();
          // Ambiguous network failure after click: do not offer retry.
          this.postingLocked = true;
          this.hideModal();
          this.outcomeUnknown = true;
        });
    },

    retryReconcile: function() {
      const id = this.rowId;
      if (!id || this.continuingReconcile) {
        return;
      }
      this.continuingReconcile = true;
      this.reconcileRetryDetail = null;
      continueReconcile(id)
        .then(result => {
          this.continuingReconcile = false;
          if (!result) {
            this.reconcileRetryDetail = "No update was returned. Check with support before trying again.";
            return;
          }
          if (result.ok === false) {
            this.reconcileRetryDetail = result.detail || result.error || "Could not continue record updates.";
            return;
          }
          const status = String(result.status || "").toLowerCase();
          this.$emit("status-progress", {
            writePlan: result.writePlan || null,
            writeResults: result.writeResults || null,
            status: result.status || null
          });
          if (status === "reconciled") {
            this.confirmedPosted = true;
            this.reconcileRetryDetail = "All three records now match.";
            this.$emit("posted", result);
            return;
          }
          this.reconcileRetryDetail = result.detail
            || "Still waiting on a record update. Posting will not be tried again.";
        })
        .catch(() => {
          this.continuingReconcile = false;
          this.reconcileRetryDetail =
            "Could not reach the server to retry record updates. The invoice was not posted again.";
        });
    },

    checkRecorded: function() {
      const id = this.rowId;
      if (!id || this.checkingStatus) {
        return;
      }
      this.checkingStatus = true;
      this.statusCheckDetail = null;
      postingStatus(id)
        .then(result => {
          this.checkingStatus = false;
          if (!result) {
            this.statusCheckDetail = "No status was returned. Check INSIGHT directly.";
            return;
          }
          const status = String(result.status || "none").toLowerCase();
          if (
            status === "confirmed"
            || status === "posted"
            || status === "post_confirmed"
            || status === "neon_recorded"
            || status === "zoho_recorded"
            || status === "reconciled"
            || status.indexOf("reconciliation_hold") >= 0
          ) {
            this.outcomeUnknown = false;
            this.confirmedInvoice =
              result.invoiceNumber || result.transactionId || this.invoiceNumber || null;
            this.statusCheckDetail = null;
            if (status === "reconciled" || status === "confirmed" || status === "posted") {
              this.confirmedPosted = true;
            }
            this.$emit("status-progress", {
              writePlan: result.writePlan || null,
              attemptId: result.attemptId || result.postingAttemptId || null,
              status: result.status || null
            });
            this.$emit("posted", Object.assign({}, result, {
              status: status === "reconciled" || status === "confirmed" || status === "posted"
                ? result.status
                : (result.status || "reconciliation_hold")
            }));
            return;
          }
          this.statusCheckDetail =
            "INSIGHT status for this billing line: " +
            status +
            (result.detail ? " — " + result.detail : "") +
            ". Do not post again.";
          this.$emit("status-checked", result);
        })
        .catch(() => {
          this.checkingStatus = false;
          this.statusCheckDetail =
            "Could not refresh status. Check what INSIGHT recorded directly — do not post again.";
        });
    },

    loadPostedAllocation: function() {
      const id = this.rowId;
      if (!id || this.allocationLoading) {
        return;
      }
      this.allocationLoading = true;
      this.allocationError = null;
      postedAllocation(id)
        .then(result => {
          this.allocationLoading = false;
          if (!result || result.ok === false) {
            this.allocationError =
              (result && (result.error || result.detail)) ||
              "Unable to read the posted allocation from INSIGHT.";
            return;
          }
          this.allocationReport = result;
        })
        .catch(() => {
          this.allocationLoading = false;
          this.allocationError =
            "Could not reach INSIGHT to read the posted allocation. Check the commission split in INSIGHT directly.";
        });
    },

    displayValue: function(value) {
      if (value === null || value === undefined || value === "") return "—";
      if (value === true) return "yes";
      if (value === false) return "no";
      return String(value);
    },

    moneyOf: function(row, field) {
      const entries = row && Array.isArray(row.money) ? row.money : [];
      return entries.filter(entry => entry.field === field)[0] || {
        expected: null,
        actual: null,
        match: false
      };
    },

    moneyClass: function(row, field) {
      return this.moneyOf(row, field).match ? "" : "text-danger";
    },

    hideModal: function() {
      const modal = this.$refs.confirmModal;
      if (modal && typeof modal.hide === "function") {
        modal.hide();
      }
    }
  }
};

/**
 * Normalise a policy number for the typed-confirm comparison. FORMATTING ONLY: trim, collapse
 * internal whitespace, fold case, and rewrite unicode dashes to an ASCII hyphen. Two materially
 * different identifiers can never normalise onto each other.
 *
 * MUST stay behaviourally identical to normalisePolicyIdentity() in
 * SERVER/services/insight-post-service.js. If the two drift, this button enables on values the
 * server rejects, which is the defect this pairing exists to prevent.
 */
function normalisePolicyIdentity(value) {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value)
    .replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

function defaultAccountingMonth() {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return String(d.getFullYear()) + month;
}

// Accept a hyphenated month typed or preset upstream (2026-07) and send the six-digit form INSIGHT
// documents. Only separators are removed - no value is invented or shifted.
function normaliseAccountingMonth(raw) {
  return String(raw == null ? "" : raw)
    .trim()
    .replace(/[-/\s]/g, "");
}

function createPostingReference() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return (
    "post-" +
    Date.now().toString(36) +
    "-" +
    Math.random()
      .toString(36)
      .slice(2, 12)
  );
}
</script>
